(function () {
  const STORAGE_DISMISSED = 'evoloop_discount_popup_dismissed';
  const STORAGE_SUBSCRIBED = 'evoloop_discount_popup_subscribed';

  class DiscountPopup extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-discount-popup-backdrop]');
      this.closeButtons = this.querySelectorAll('[data-discount-popup-close]');
      this.form = this.querySelector('#DiscountPopupCustomerForm');
      this.emailInput = this.querySelector('[data-discount-popup-email]');
      this.submitBtn = this.querySelector('[data-discount-popup-submit]');
      this.errorEl = this.querySelector('[data-discount-popup-error]');
      this.successPanel = this.querySelector('[data-discount-popup-success]');
      this.copyBtn = this.querySelector('[data-discount-popup-copy]');
      this.codeEl = this.querySelector('[data-discount-popup-code]');

      this.delay = parseInt(this.dataset.delay, 10) || 5000;
      this.discountCode = this.dataset.discountCode || 'WELCOME10';
      this.homepageOnly = this.dataset.homepageOnly === 'true';
      this.isHomepage = this.dataset.isHomepage === 'true';

      if (this.homepageOnly && !this.isHomepage) return;
      if (this.shouldSkip()) return;

      this.closeButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.dismiss();
        });
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.dismiss());
      }

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) {
          this.dismiss();
        }
      });

      if (this.form) {
        this.form.addEventListener('submit', (e) => this.handleSubmit(e));
      }

      if (this.copyBtn && this.codeEl) {
        this.copyBtn.addEventListener('click', () => this.copyCode());
      }

      this.timer = window.setTimeout(() => this.open(), this.delay);
    }

    disconnectedCallback() {
      if (this.timer) window.clearTimeout(this.timer);
    }

    shouldSkip() {
      if (window.Shopify && window.Shopify.designMode) return false;
      try {
        if (localStorage.getItem(STORAGE_DISMISSED)) return true;
        if (localStorage.getItem(STORAGE_SUBSCRIBED)) return true;
      } catch (e) {
        /* localStorage unavailable */
      }
      return false;
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('discount-popup-open');
      const focusTarget = this.emailInput || this.querySelector('[data-discount-popup-close]');
      if (focusTarget) focusTarget.focus();
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('discount-popup-open');
    }

    dismiss() {
      try {
        localStorage.setItem(STORAGE_DISMISSED, '1');
      } catch (e) {
        /* ignore */
      }
      this.close();
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    showSuccess() {
      if (this.form) this.form.classList.add('is-hidden');
      if (this.successPanel) this.successPanel.classList.add('is-visible');
      if (this.codeEl) this.codeEl.textContent = this.discountCode;
      try {
        localStorage.setItem(STORAGE_SUBSCRIBED, '1');
      } catch (e) {
        /* ignore */
      }
    }

    async handleSubmit(event) {
      event.preventDefault();
      this.hideError();

      const email = (this.emailInput?.value || '').trim();
      if (!email) {
        this.showError(this.dataset.errorEmpty || 'Please enter your email.');
        return;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.showError(this.dataset.errorInvalid || 'Please enter a valid email address.');
        return;
      }

      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        this.submitBtn.setAttribute('aria-busy', 'true');
      }

      const honeypot = this.form.querySelector('[name="contact[body]"]');
      if (honeypot && honeypot.value) {
        this.showError(this.dataset.errorGeneric || 'Something went wrong. Please try again.');
        if (this.submitBtn) {
          this.submitBtn.disabled = false;
          this.submitBtn.removeAttribute('aria-busy');
        }
        return;
      }

      const body = new URLSearchParams(new FormData(this.form));

      try {
        const formAction = this.form.getAttribute('action') || `${window.shopUrl}/contact`;
        const response = await fetch(formAction, {
          method: 'POST',
          headers: {
            Accept: 'text/html',
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: body.toString(),
          credentials: 'same-origin',
        });

        const html = await response.text();
        const subscribed = this.isSubscriptionSuccess(html, response);

        if (subscribed) {
          this.showSuccess();
        } else {
          this.showError(this.dataset.errorGeneric || 'Something went wrong. Please try again.');
        }
      } catch (err) {
        this.showError(this.dataset.errorGeneric || 'Something went wrong. Please try again.');
      } finally {
        if (this.submitBtn) {
          this.submitBtn.disabled = false;
          this.submitBtn.removeAttribute('aria-busy');
        }
      }
    }

    isSubscriptionSuccess(html, response) {
      if (
        response.url.includes('customer_posted=true') ||
        html.includes('newsletter-form__message--success') ||
        html.includes('customer_posted=true') ||
        html.includes('form-posted-success')
      ) {
        return true;
      }

      const lower = html.toLowerCase();
      if (
        lower.includes('already subscribed') ||
        lower.includes('already been taken') ||
        lower.includes('is already associated')
      ) {
        return true;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const errorNode = doc.querySelector('.errors, .form__message--error, [data-discount-popup-error]');
      if (!errorNode && response.ok) return true;

      return false;
    }

    async copyCode() {
      const code = this.discountCode;
      try {
        await navigator.clipboard.writeText(code);
        this.copyBtn.textContent = this.dataset.copiedLabel || 'Copied!';
        this.copyBtn.classList.add('is-copied');
        window.setTimeout(() => {
          this.copyBtn.textContent = this.dataset.copyLabel || 'Copy';
          this.copyBtn.classList.remove('is-copied');
        }, 2000);
      } catch (e) {
        /* fallback */
        const range = document.createRange();
        range.selectNode(this.codeEl);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
        document.execCommand('copy');
      }
    }
  }

  if (!customElements.get('discount-popup')) {
    customElements.define('discount-popup', DiscountPopup);
  }
})();
